import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Heart, 
  Users, 
  Briefcase, 
  Star, 
  MessageCircle, 
  Calendar,
  Stethoscope,
  BookOpen,
  AlertCircle
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

const QUILTT_LOGO = 'https://cdn.prod.website-files.com/6005bb130071e76d42c92ec9/608bf8a812544a0b1a4c7b3e_quiltt-logo-horiz.png';

export default function CareCardPage() {
  const { token } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPage();
  }, [token]);

  const loadPage = async () => {
    try {
      const pageDoc = await getDoc(doc(db, 'publicPages', token));
      
      if (!pageDoc.exists()) {
        setError('This page was not found or has been removed.');
        setLoading(false);
        return;
      }
      
      const data = pageDoc.data();
      if (data.type !== 'careCard') {
        setError('Invalid page type.');
        setLoading(false);
        return;
      }
      
      setPageData(data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading Quiltt Card:', err);
      setError('Failed to load Quiltt Card.');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-quiltt-purple border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-quiltt-navy mb-2">Page Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const content = pageData.content || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <img 
            src={QUILTT_LOGO} 
            alt="Quiltt" 
            className="h-8"
          />
          <span className="text-sm text-quiltt-purple font-medium">Quiltt Card</span>
        </div>
      </header>

      {/* Profile Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="flex items-center gap-4">
            {pageData.profilePhotoUrl ? (
              <img
                src={pageData.profilePhotoUrl}
                alt={pageData.residentName}
                className="w-20 h-20 rounded-full object-cover border-4 border-quiltt-purple/20"
              />
            ) : (
              <div className="w-20 h-20 bg-quiltt-purple/10 rounded-full flex items-center justify-center">
                <Heart className="w-10 h-10 text-quiltt-purple" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-quiltt-navy">{pageData.residentName}</h1>
              <p className="text-gray-500 mt-1">Staff Reference Guide</p>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="space-y-4">
          {/* Background */}
          {content.background && (
            <Section icon={BookOpen} title="Background">
              <p className="text-gray-700">{content.background}</p>
            </Section>
          )}

          {/* Family */}
          {content.family && (
            <Section icon={Users} title="Family">
              <p className="text-gray-700">{content.family}</p>
            </Section>
          )}

          {/* Career Highlights */}
          {content.careerHighlights && (
            <Section icon={Briefcase} title="Career & Achievements">
              <p className="text-gray-700">{content.careerHighlights}</p>
            </Section>
          )}

          {/* Preferences */}
          {content.preferences && (
            <Section icon={Star} title="Preferences & Likes">
              <p className="text-gray-700">{content.preferences}</p>
            </Section>
          )}

          {/* Communication Tips */}
          {content.communicationTips && (
            <Section icon={MessageCircle} title="Communication Tips">
              <p className="text-gray-700">{content.communicationTips}</p>
            </Section>
          )}

          {/* Important Dates */}
          {content.importantDates && (
            <Section icon={Calendar} title="Important Dates">
              <p className="text-gray-700">{content.importantDates}</p>
            </Section>
          )}

          {/* Health Notes */}
          {content.healthNotes && (
            <Section icon={Stethoscope} title="Health & Dietary Notes">
              <p className="text-gray-700">{content.healthNotes}</p>
            </Section>
          )}

          {/* Spiritual/Cultural */}
          {content.spiritualCultural && (
            <Section icon={Heart} title="Spiritual & Cultural">
              <p className="text-gray-700">{content.spiritualCultural}</p>
            </Section>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-200 text-center">
          <img 
            src={QUILTT_LOGO} 
            alt="Quiltt" 
            className="h-5 mx-auto opacity-40"
          />
          <p className="text-xs text-gray-400 mt-2">Life Story Capture</p>
        </div>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-quiltt-purple/10 rounded-xl flex items-center justify-center">
            <Icon className="w-5 h-5 text-quiltt-purple" />
          </div>
          <h2 className="text-lg font-semibold text-quiltt-navy">{title}</h2>
        </div>
        {children}
      </div>
    </div>
  );
}
